import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Select, Switch, Space, Typography, Row, Col, Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { PictureOutlined } from '@ant-design/icons';
import { usePrevious } from 'hooks';
import { ImageUpload } from 'components/common';
import { Formik } from 'formik';
import { createMessageTemplateRequest, updateMessageTemplateRequest } from 'providers/MessageTemplateProvider/slice';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// Wrapper component to make ImageUpload work with Ant Design Form
const AntdImageUploadWrapper = ({ value, onChange }) => {
  const formikRef = React.useRef();

  return (
    <div style={{ position: 'relative' }}>
      <Formik initialValues={{ image: value }} enableReinitialize onSubmit={() => {}} innerRef={formikRef}>
        {({ values }) => {
          // Check if values changed and notify parent
          if (formikRef.current && values.image !== value) {
            onChange?.(values.image);
            // Update the ref to prevent infinite loops
            formikRef.current.setFieldValue('image', values.image, false);
          }

          return <ImageUpload name="image" aspectRatio={16 / 9} disableCrop={false} />;
        }}
      </Formik>
    </div>
  );
};

const MessageTemplateModal = ({ visible, onCancel, onSuccess, editingTemplate }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [previewContent, setPreviewContent] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  const creating = useSelector((state) => state.messageTemplate.creating);
  const updating = useSelector((state) => state.messageTemplate.updating);
  const error = useSelector((state) => state.messageTemplate.error);

  const isLoading = creating || updating;

  useEffect(() => {
    if (visible) {
      // Check for different possible image property names
      const possibleImageProps = ['image', 'imageUrl', 'image_url', 'attachment', 'media', 'thumbnail'];
      let imageData = null;

      for (const prop of possibleImageProps) {
        if (editingTemplate?.[prop]) {
          imageData = editingTemplate[prop];
          break;
        }
      }

      if (editingTemplate) {
        form.setFieldsValue({
          title: editingTemplate.title,
          content: editingTemplate.content,
          category: editingTemplate.category,
          isActive: editingTemplate.isActive,
          image: imageData,
        });
        setTags(editingTemplate.tags || []);
        setPreviewContent(editingTemplate.content || '');
        setUploadedImage(imageData);
      } else {
        form.resetFields();
        form.setFieldsValue({
          title: '',
          content: '',
          category: 'greeting',
          isActive: true,
          image: undefined,
        });
        setTags([]);
        setPreviewContent('');
        setUploadedImage(null);
      }
    }
  }, [visible, editingTemplate, form]);

  // Watch for successful completion and close modal
  const prevCreating = usePrevious(creating);
  const prevUpdating = usePrevious(updating);

  useEffect(() => {
    // If was creating/updating and now not (and no error), operation was successful
    if ((prevCreating && !creating && !error) || (prevUpdating && !updating && !error)) {
      form.resetFields();
      setTags([]);
      setPreviewContent('');
      setUploadedImage(null);
      onSuccess();
    }
  }, [creating, updating, error, prevCreating, prevUpdating, form, onSuccess]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (removedTag) => {
    setTags(tags.filter((tag) => tag !== removedTag));
  };

  const handleContentChange = (e) => {
    const content = e.target.value;
    setPreviewContent(renderPreviewContent(content));
  };

  const renderPreviewContent = (content) => {
    if (!content) return '';

    // Replace placeholders with example values for preview
    const placeholders = {
      '{customer_name}': 'Nguyễn Văn A',
      '{product_name}': 'iPhone 15 Pro',
      '{order_id}': 'DH123456',
      '{price}': '25.000.000 VNĐ',
      '{date}': new Date().toLocaleDateString('vi-VN'),
      '{phone}': '0123456789',
      '{address}': '123 Nguyễn Văn Cừ, Q.5, TP.HCM',
      '{time}': new Date().toLocaleTimeString('vi-VN'),
    };

    let preview = content;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return preview;
  };

  const handleImageChange = (imageData) => {
    setUploadedImage(imageData);
  };

  const handleSubmit = (values) => {
    // Try to get image from form values if uploadedImage is null
    const formImage = form.getFieldValue('image');

    // Extract only the URL from the image data
    let imageUrl = null;
    if (uploadedImage) {
      imageUrl = uploadedImage.url || uploadedImage;
    } else if (formImage) {
      imageUrl = formImage.url || formImage;
    }

    const templateData = {
      ...values,
      tags,
    };

    // Only add image field if there's actually an image
    if (imageUrl) {
      templateData.image = imageUrl;
    }

    // Remove any id fields for create operation
    if (!editingTemplate) {
      delete templateData.id;
      delete templateData._id;
    }

    if (editingTemplate) {
      dispatch(
        updateMessageTemplateRequest({
          id: editingTemplate._id,
          ...templateData,
        })
      );
    } else {
      dispatch(createMessageTemplateRequest(templateData));
    }

    // Modal will close automatically via useEffect when operation completes successfully
  };

  return (
    <Modal
      title={editingTemplate ? 'Chỉnh sửa template' : 'Tạo template mới'}
      visible={visible}
      onCancel={onCancel}
      width={900}
      footer={null}
      className="message-template-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          isActive: true,
          category: 'greeting',
        }}
      >
        <Row gutter={24}>
          {/* Left Column */}
          <Col span={10}>
            <Form.Item
              name="title"
              label="Tiêu đề template"
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
            >
              <Input placeholder="Nhập tiêu đề" />
            </Form.Item>

            <Form.Item name="category" label="Danh mục" rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}>
              <Select placeholder="Nhập tên nhãn dán..." className="category-select">
                <Option value="greeting">Chào hỏi</Option>
                <Option value="order">Đơn hàng</Option>
                <Option value="support">Hỗ trợ</Option>
                <Option value="promotion">Khuyến mãi</Option>
                <Option value="notification">Thông báo</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Tags">
              <Input.Group compact style={{ display: 'flex' }}>
                <Input
                  placeholder="Nhập tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onPressEnter={handleAddTag}
                  style={{ width: 'calc(100% - 84px)', height: 32, borderRadius: 6 }}
                />
                <Button
                  type="primary"
                  onClick={handleAddTag}
                  style={{
                    backgroundColor: '#26415D',
                    borderColor: '#26415D',
                    width: 80,
                    height: 32,
                    borderRadius: 4,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  + Thêm
                </Button>
              </Input.Group>
              {tags.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: 'inline-block',
                        background: '#f0f0f0',
                        padding: '4px 8px',
                        margin: '2px',
                        borderRadius: '4px',
                        fontSize: '12px',
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        style={{
                          border: 'none',
                          background: 'none',
                          marginLeft: '8px',
                          cursor: 'pointer',
                          color: '#999',
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>
              <span>Trạng thái hoạt động</span>
              <Form.Item name="isActive" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch className="custom-switch-blue" />
              </Form.Item>
            </div>
            <style>{`
              .custom-switch-blue.ant-switch-checked {
                background-color: #406AFF !important;
              }
              .message-template-modal .ant-btn-primary {
                background-color: #26415D !important;
                border-color: #26415D !important;
              }
              .message-template-modal .ant-btn-primary:hover {
                background-color: #3a5a7a !important;
                border-color: #3a5a7a !important;
              }
              .category-select.ant-select .ant-select-selector {
                height: 32px !important;
                border-radius: 6px !important;
              }
              .category-select.ant-select .ant-select-selection-search-input,
              .category-select.ant-select .ant-select-selection-item,
              .category-select.ant-select .ant-select-selection-placeholder {
                line-height: 30px !important;
              }
            `}</style>
          </Col>

          {/* Right Column */}
          <Col span={14}>
            <Form.Item
              name="content"
              label="Nội dung Template"
              rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
            >
              <TextArea
                rows={8}
                placeholder="Nhập nội dung"
                onChange={handleContentChange}
                maxLength={1000}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="image"
              label={
                <span>
                  <PictureOutlined style={{ marginRight: 4 }} />
                  Thêm hình ảnh
                </span>
              }
            >
              <AntdImageUploadWrapper value={uploadedImage} onChange={handleImageChange} />
            </Form.Item>
          </Col>
        </Row>

        {/* Preview Section */}
        <div style={{ marginTop: 16 }}>
          <Checkbox checked={showPreview} onChange={(e) => setShowPreview(e.target.checked)}>
            Hiển thị xem trước
          </Checkbox>
          {showPreview && (previewContent || uploadedImage) && (
            <div style={{ marginTop: 12 }}>
              <Text strong>Xem trước:</Text>
              <div className="template-preview preview-active" style={{ marginTop: 8 }}>
                {uploadedImage && (
                  <div style={{ marginBottom: 12 }}>
                    <img
                      src={
                        typeof uploadedImage === 'string'
                          ? uploadedImage
                          : uploadedImage?.url || uploadedImage?.imageUrl || uploadedImage?.image_url
                      }
                      alt="Template"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        borderRadius: 4,
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                )}
                {previewContent}
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <Form.Item style={{ marginBottom: 0, marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>Huỷ</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Xác nhận
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MessageTemplateModal;
